import React, { useState, useMemo, memo } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

const CustomSelect = ({ label, value, options = [], onChange, id, disabled = false }) => {
  const [highlightedOption, setHighlightedOption] = useState(null);
  const [open, setOpen] = useState(false);
  const filteredOptionsRef = React.useRef([]);
  const hasUserTypedRef = React.useRef(false);
  const highlightReasonRef = React.useRef(null);

  // Normalize options to uniform { label, value } objects
  const normalizedOptions = useMemo(() => {
    return (options || []).map((opt) => {
      if (typeof opt === 'string') {
        return { label: opt, value: opt };
      }
      const val = opt?.ValueCode ?? opt?.value ?? opt?.id ?? '';
      const lbl = opt?.DisplayText ?? opt?.label ?? opt?.name ?? String(val);
      return {
        ...opt,
        label: lbl,
        value: String(val),
      };
    });
  }, [options]);

  const selectedOption = useMemo(() => {
    if (value === undefined || value === null || value === '') {
      return normalizedOptions[0] || null;
    }
    const strVal = String(value).toLowerCase();
    return (
      normalizedOptions.find(
        (opt) =>
          String(opt.value).toLowerCase() === strVal ||
          String(opt.label).toLowerCase() === strVal
      ) || normalizedOptions[0] || null
    );
  }, [normalizedOptions, value]);

  const handleSelectOption = (optionToSelect) => {
    if (optionToSelect) {
      onChange?.(optionToSelect.value, optionToSelect);
    }
    setOpen(false);
    setHighlightedOption(null);
    hasUserTypedRef.current = false;
    highlightReasonRef.current = null;
  };

  const handleInternalKeyDown = (e) => {
    if (e.key === 'Tab') {
      if (hasUserTypedRef.current) {
        const optionToSelect = highlightedOption || (filteredOptionsRef.current && filteredOptionsRef.current[0]);
        if (optionToSelect) {
          handleSelectOption(optionToSelect);
        } else {
          setOpen(false);
          setHighlightedOption(null);
          hasUserTypedRef.current = false;
          highlightReasonRef.current = null;
        }
      } else if (highlightReasonRef.current === 'keyboard' && highlightedOption) {
        handleSelectOption(highlightedOption);
      } else {
        setOpen(false);
        setHighlightedOption(null);
        hasUserTypedRef.current = false;
        highlightReasonRef.current = null;
      }
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (hasUserTypedRef.current) {
        const optionToSelect = highlightedOption || (filteredOptionsRef.current && filteredOptionsRef.current[0]);
        if (optionToSelect) {
          handleSelectOption(optionToSelect);
        }
      } else if (highlightReasonRef.current === 'keyboard' && highlightedOption) {
        handleSelectOption(highlightedOption);
      } else if (open) {
        const optionToSelect = highlightedOption || (filteredOptionsRef.current && filteredOptionsRef.current[0]);
        if (optionToSelect) {
          handleSelectOption(optionToSelect);
        }
      }
    }
  };

  return (
    <Autocomplete
      id={id}
      size="small"
      fullWidth
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => {
        setOpen(false);
        setHighlightedOption(null);
        hasUserTypedRef.current = false;
        highlightReasonRef.current = null;
      }}
      onInputChange={(event, newInputValue, reason) => {
        if (reason === 'input') {
          hasUserTypedRef.current = true;
        } else if (reason === 'reset' || reason === 'clear') {
          hasUserTypedRef.current = false;
        }
      }}
      disableClearable
      disabled={disabled}
      options={normalizedOptions}
      value={selectedOption}
      onChange={(e, newValue) => {
        setHighlightedOption(null);
        hasUserTypedRef.current = false;
        highlightReasonRef.current = null;
        if (newValue) {
          onChange?.(newValue.value, newValue);
        }
      }}
      getOptionLabel={(option) => option?.label || ''}
      onHighlightChange={(event, option, reason) => {
        if (reason !== 'mouse') {
          setHighlightedOption(option);
          highlightReasonRef.current = reason;
        }
      }}
      filterOptions={(opts, state) => {
        const query = String(state.inputValue || '').toLowerCase().trim();
        const filtered = query
          ? opts.filter(opt => String(opt.label || '').toLowerCase().includes(query))
          : opts;
        filteredOptionsRef.current = filtered;
        return filtered;
      }}
      autoHighlight
      isOptionEqualToValue={(option, val) =>
        String(option?.value).toLowerCase() === String(val?.value).toLowerCase()
      }
      renderOption={(props, option, state) => {
        const { key, ...restProps } = props;
        const uniqueKey = option?.value || `${option?.label || 'opt'}-${state?.index ?? ''}`;
        const isSep = option?.optionType === 'separator' || Boolean(option?.isSeparator);
        const label = option?.label || '';
        return (
          <li
            key={uniqueKey}
            {...restProps}
            title={label}
            style={{
              ...restProps.style,
              ...(isSep ? { pointerEvents: 'none', color: '#999', fontSize: '11px', minHeight: '20px' } : {})
            }}
          >
            <span title={label}>{label}</span>
          </li>
        );
      }}
      slotProps={{
        paper: {
          sx: {
            borderRadius: 1,
          },
        },
        popper: {
          placement: 'bottom-start',
          modifiers: [{ name: 'flip', enabled: true }],
        },
      }}
      ListboxProps={{
        sx: {
          maxHeight: 200,
          overflowY: 'auto',
          p: 0,
          '& .MuiAutocomplete-option': {
            fontSize: '0.7rem',
            lineHeight: '18px',
            paddingTop: '5px',
            paddingBottom: '5px',
            paddingLeft: '8px',
            paddingRight: '8px',
            borderBottom: '1px solid #d8d8d8',
          },
          '& .MuiAutocomplete-option:last-child': {
            borderBottom: 'none',
          },
          '& .MuiAutocomplete-option[aria-selected="true"]': {
            backgroundColor: '#2f5d8a !important',
            color: 'white',
          },
          '&::-webkit-scrollbar': {
            width: 6,
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#bdbdbd',
            borderRadius: 0,
          },
          '&::-webkit-scrollbar-thumb:hover': {
            backgroundColor: '#9e9e9e',
          },
        },
      }}
      popupIcon={<ArrowDropDownIcon sx={{ color: 'rgba(0, 0, 0, 0.54)', fontSize: '20px' }} />}
      renderInput={(params) => {
        const labelText = params.inputProps?.value || selectedOption?.label || '';
        return (
          <span title={labelText || undefined} style={{ width: '100%', display: 'inline-block' }}>
            <TextField
              {...params}
              title={labelText || undefined}
              inputProps={{
                ...params.inputProps,
                title: labelText || undefined,
              }}
              label={label}
              size="small"
              onKeyDown={handleInternalKeyDown}
              sx={{
                width: '100%',
                '& .MuiOutlinedInput-root': {
                  height: 40,
                  backgroundColor: disabled ? '#E0E0E0' : '#f5f5f5',
                  fontSize: '0.7rem',
                  fontWeight: 550,
                  paddingRight: '36px !important',
                  '& fieldset': {
                    borderColor: 'rgba(0, 0, 0, 0.23)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(0, 0, 0, 0.87)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#1976d2',
                  },
                  '&.Mui-disabled': {
                    backgroundColor: '#E0E0E0 !important',
                  },
                },
                '& .MuiInputLabel-root': {
                  fontSize: '0.875rem',
                  lineHeight: '1.2',
                },
                '& .MuiInputLabel-shrink': {
                  fontSize: '0.875rem',
                },
                '& .MuiOutlinedInput-input': {
                  fontSize: '0.7rem',
                  fontWeight: 550,
                  paddingTop: '8px !important',
                  paddingBottom: '8px !important',
                  '&.Mui-disabled': {
                    WebkitTextFillColor: '#000 !important',
                    color: '#000 !important',
                  },
                },
              }}
            />
          </span>
        );
      }}
    />
  );
};

export default memo(CustomSelect);
